<script setup>
/**
 * PipelineMetricsPanel — slide-over à direita com as métricas agregadas do
 * pipeline (consome `GET .../pipelines/:id/metrics`). Reusa a mesma mecânica de
 * slide-over da FeatureGuide (teleport + scrim + animação + fechar no Esc/scrim)
 * para um UX consistente, sem novas dependências.
 *
 * O carregamento dos dados é feito pelo board (que injeta `metrics` e
 * `loading`) — assim este componente fica só de apresentação.
 */
import { ref, watch, nextTick, onBeforeUnmount, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEventListener } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { formatMoney } from './utils/currency';

const props = defineProps({
  open: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  // Shape: { stages:[{id,name,behavior,count,total_value,weighted_value}],
  //   summary:{total_count,open_value,forecast,won_count,won_value,lost_count,
  //   win_rate} }
  metrics: { type: Object, default: null },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const close = () => emit('close');

const summary = computed(() => props.metrics?.summary || {});
const stages = computed(() => props.metrics?.stages || []);

// win_rate vem como fração (0-1) ou null. Mostramos em % com 1 casa; null = "—".
const winRateLabel = computed(() => {
  const rate = summary.value.win_rate;
  if (rate === null || rate === undefined) return '—';
  return `${(Number(rate) * 100).toFixed(1)}%`;
});

// Cartões de destaque do topo (resumo).
const summaryCards = computed(() => [
  {
    key: 'forecast',
    icon: 'i-lucide-trending-up',
    label: t('PIPELINES.METRICS.FORECAST'),
    value: formatMoney(summary.value.forecast),
    accent: 'text-n-teal-11',
  },
  {
    key: 'open',
    icon: 'i-lucide-circle-dollar-sign',
    label: t('PIPELINES.METRICS.OPEN_VALUE'),
    value: formatMoney(summary.value.open_value),
    accent: 'text-n-blue-11',
  },
  {
    key: 'won',
    icon: 'i-lucide-trophy',
    label: t('PIPELINES.METRICS.WON_VALUE'),
    value: formatMoney(summary.value.won_value),
    accent: 'text-n-teal-11',
  },
  {
    key: 'winRate',
    icon: 'i-lucide-percent',
    label: t('PIPELINES.METRICS.WIN_RATE'),
    value: winRateLabel.value,
    accent: 'text-n-slate-12',
  },
]);

// Contagens de negócios (total / ganhos / perdidos). O ":" fica no JS (não no
// template) para não disparar o lint de raw-text do vue-i18n.
const countItems = computed(() => [
  {
    key: 'total',
    icon: 'i-lucide-layers',
    iconClass: 'text-n-slate-10',
    label: `${t('PIPELINES.METRICS.TOTAL_COUNT')}:`,
    value: summary.value.total_count || 0,
  },
  {
    key: 'won',
    icon: 'i-lucide-trophy',
    iconClass: 'text-n-teal-10',
    label: `${t('PIPELINES.METRICS.WON_COUNT')}:`,
    value: summary.value.won_count || 0,
  },
  {
    key: 'lost',
    icon: 'i-lucide-x-circle',
    iconClass: 'text-n-ruby-10',
    label: `${t('PIPELINES.METRICS.LOST_COUNT')}:`,
    value: summary.value.lost_count || 0,
  },
]);

// Mesma guarda anti "fecha na hora" da FeatureGuide.
const isReady = ref(false);
const onScrimClick = () => {
  if (isReady.value) close();
};
const onKeydown = event => {
  if (event.key === 'Escape' && isReady.value) close();
};

watch(
  () => props.open,
  async isOpen => {
    isReady.value = false;
    if (!isOpen) return;
    await nextTick();
    isReady.value = true;
  },
  { immediate: true }
);

useEventListener(document, 'keydown', onKeydown);

onBeforeUnmount(() => {
  isReady.value = false;
});

const behaviorAccent = behavior => {
  if (behavior === 'won') return 'text-n-teal-11';
  if (behavior === 'lost') return 'text-n-ruby-11';
  return 'text-n-slate-11';
};
</script>

<template>
  <TeleportWithDirection to="body">
    <div v-if="open" class="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        :aria-label="t('PIPELINES.METRICS.CLOSE')"
        class="absolute inset-0 border-0 cursor-default bg-n-alpha-black1 backdrop-blur-[4px]"
        @click="onScrimClick"
      />

      <aside
        role="dialog"
        aria-modal="true"
        class="relative flex flex-col w-full h-full max-w-md shadow-xl bg-n-background ltr:border-l rtl:border-r border-n-weak animate-slide-in-right"
        @click.stop
      >
        <header
          class="flex items-center gap-3 px-6 py-4 border-b border-n-weak shrink-0"
        >
          <span
            class="grid rounded-lg size-9 place-items-center bg-n-alpha-2 text-n-brand"
          >
            <span class="size-5 i-lucide-bar-chart-3" />
          </span>
          <div class="flex-grow min-w-0">
            <h2 class="text-base font-medium truncate text-n-slate-12">
              {{ t('PIPELINES.METRICS.TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-11">
              {{ t('PIPELINES.METRICS.SUBTITLE') }}
            </p>
          </div>
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            :aria-label="t('PIPELINES.METRICS.CLOSE')"
            @click="close"
          />
        </header>

        <div class="flex-grow px-6 py-5 overflow-y-auto">
          <div
            v-if="loading"
            class="flex items-center justify-center py-12 text-sm text-n-slate-11"
          >
            <span class="mr-2 size-4 i-lucide-loader-2 animate-spin" />
            {{ t('PIPELINES.METRICS.LOADING') }}
          </div>

          <template v-else>
            <!-- Cartões de resumo -->
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="card in summaryCards"
                :key="card.key"
                class="flex flex-col gap-1 p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-2"
              >
                <span class="flex items-center gap-1.5 text-xs text-n-slate-11">
                  <span class="size-3.5" :class="card.icon" />
                  {{ card.label }}
                </span>
                <span
                  class="text-lg font-semibold tabular-nums"
                  :class="card.accent"
                >
                  {{ card.value }}
                </span>
              </div>
            </div>

            <!-- Contagens (negócios totais / ganhos / perdidos) -->
            <div
              class="flex flex-wrap gap-4 px-4 py-3 mt-3 text-sm rounded-xl outline outline-1 outline-n-weak bg-n-solid-2"
            >
              <span
                v-for="item in countItems"
                :key="item.key"
                class="flex items-center gap-1.5 text-n-slate-11"
              >
                <span class="size-4" :class="[item.icon, item.iconClass]" />
                <span>{{ item.label }}</span>
                <strong class="text-n-slate-12">{{ item.value }}</strong>
              </span>
            </div>

            <!-- Tabela por estágio -->
            <h3
              class="mt-6 mb-2 text-xs font-medium tracking-wide uppercase text-n-slate-11"
            >
              {{ t('PIPELINES.METRICS.BY_STAGE') }}
            </h3>
            <div
              class="overflow-hidden rounded-xl outline outline-1 outline-n-weak"
            >
              <table class="w-full text-sm border-collapse">
                <thead>
                  <tr class="bg-n-solid-2 text-n-slate-11">
                    <th
                      class="px-3 py-2 font-medium ltr:text-left rtl:text-right"
                    >
                      {{ t('PIPELINES.METRICS.STAGE') }}
                    </th>
                    <th class="px-3 py-2 font-medium text-right">
                      {{ t('PIPELINES.METRICS.COUNT') }}
                    </th>
                    <th class="px-3 py-2 font-medium text-right">
                      {{ t('PIPELINES.METRICS.VALUE') }}
                    </th>
                    <th class="px-3 py-2 font-medium text-right">
                      {{ t('PIPELINES.METRICS.WEIGHTED') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="stage in stages"
                    :key="stage.id"
                    class="border-t border-n-weak"
                  >
                    <td class="px-3 py-2">
                      <span
                        class="font-medium"
                        :class="behaviorAccent(stage.behavior)"
                      >
                        {{ stage.name }}
                      </span>
                    </td>
                    <td
                      class="px-3 py-2 text-right tabular-nums text-n-slate-12"
                    >
                      {{ stage.count }}
                    </td>
                    <td
                      class="px-3 py-2 text-right tabular-nums text-n-slate-12"
                    >
                      {{ formatMoney(stage.total_value) }}
                    </td>
                    <td
                      class="px-3 py-2 text-right tabular-nums text-n-slate-11"
                    >
                      {{ formatMoney(stage.weighted_value) }}
                    </td>
                  </tr>
                  <tr v-if="!stages.length">
                    <td
                      colspan="4"
                      class="px-3 py-6 text-center text-n-slate-10"
                    >
                      {{ t('PIPELINES.METRICS.EMPTY') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </aside>
    </div>
  </TeleportWithDirection>
</template>

<style scoped>
@keyframes slide-in-right {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.animate-slide-in-right {
  animation: slide-in-right 0.25s ease-out;
}

[dir='rtl'] .animate-slide-in-right {
  animation-name: slide-in-left;
}

@keyframes slide-in-left {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}
</style>
